import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Key, Plus, Edit, Trash2, Search, Filter,
  CheckCircle, XCircle, RefreshCw, ChevronLeft, ChevronRight,
  Shield, Lock, Unlock, Info, AlertTriangle, Package, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import permissionService from '../services/permissionService';
import { useAuth } from '../contexts/AuthContext';
import { isSuperAdmin } from '../utils/roleRedirect';
import { TableLoader } from '../components/CommonLoader';

const Permissions = () => {
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [totalPermissions, setTotalPermissions] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    category: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [categories, setCategories] = useState([]);

  // Sample data
  const samplePermissions = [
    // Users
    { id: 1, name: 'view_users', display_name: 'View Users', description: 'Can view user list and details', category: 'Users', created_at: '2024-01-15T10:00:00Z' },
    { id: 2, name: 'create_users', display_name: 'Create Users', description: 'Can create new users', category: 'Users', created_at: '2024-01-15T10:00:00Z' },
    { id: 3, name: 'edit_users', display_name: 'Edit Users', description: 'Can edit user information', category: 'Users', created_at: '2024-01-15T10:00:00Z' },
    { id: 4, name: 'delete_users', display_name: 'Delete Users', description: 'Can delete users', category: 'Users', created_at: '2024-01-15T10:00:00Z' },
    { id: 5, name: 'impersonate_users', display_name: 'Impersonate Users', description: 'Can login as another user', category: 'Users', created_at: '2024-01-15T10:00:00Z' },
    
    // Roles
    { id: 6, name: 'view_roles', display_name: 'View Roles', description: 'Can view roles list', category: 'Roles', created_at: '2024-01-15T10:00:00Z' },
    { id: 7, name: 'create_roles', display_name: 'Create Roles', description: 'Can create new roles', category: 'Roles', created_at: '2024-01-15T10:00:00Z' },
    { id: 8, name: 'edit_roles', display_name: 'Edit Roles', description: 'Can edit roles', category: 'Roles', created_at: '2024-01-15T10:00:00Z' },
    { id: 9, name: 'delete_roles', display_name: 'Delete Roles', description: 'Can delete roles', category: 'Roles', created_at: '2024-01-15T10:00:00Z' },
    { id: 10, name: 'assign_permissions', display_name: 'Assign Permissions', description: 'Can assign permissions to roles', category: 'Roles', created_at: '2024-01-15T10:00:00Z' },
    
    // Permissions
    { id: 11, name: 'view_permissions', display_name: 'View Permissions', description: 'Can view permissions list', category: 'Permissions', created_at: '2024-01-15T10:00:00Z' },
    { id: 12, name: 'create_permissions', display_name: 'Create Permissions', description: 'Can create new permissions', category: 'Permissions', created_at: '2024-01-15T10:00:00Z' },
    { id: 13, name: 'edit_permissions', display_name: 'Edit Permissions', description: 'Can edit permissions', category: 'Permissions', created_at: '2024-01-15T10:00:00Z' },
    { id: 14, name: 'delete_permissions', display_name: 'Delete Permissions', description: 'Can delete permissions', category: 'Permissions', created_at: '2024-01-15T10:00:00Z' },
    
    // Datarooms
    { id: 15, name: 'view_datarooms', display_name: 'View Datarooms', description: 'Can view datarooms', category: 'Datarooms', created_at: '2024-01-16T10:00:00Z' },
    { id: 16, name: 'create_datarooms', display_name: 'Create Datarooms', description: 'Can create new datarooms', category: 'Datarooms', created_at: '2024-01-16T10:00:00Z' },
    { id: 17, name: 'edit_datarooms', display_name: 'Edit Datarooms', description: 'Can edit datarooms', category: 'Datarooms', created_at: '2024-01-16T10:00:00Z' },
    { id: 18, name: 'delete_datarooms', display_name: 'Delete Datarooms', description: 'Can delete datarooms', category: 'Datarooms', created_at: '2024-01-16T10:00:00Z' },
    { id: 19, name: 'approve_datarooms', display_name: 'Approve Datarooms', description: 'Can approve pending datarooms', category: 'Datarooms', created_at: '2024-01-16T10:00:00Z' },
    
    // Subscriptions
    { id: 20, name: 'view_subscriptions', display_name: 'View Subscriptions', description: 'Can view subscriptions', category: 'Subscriptions', created_at: '2024-01-17T10:00:00Z' },
    { id: 21, name: 'manage_subscriptions', display_name: 'Manage Subscriptions', description: 'Can manage subscriptions', category: 'Subscriptions', created_at: '2024-01-17T10:00:00Z' },
    { id: 22, name: 'cancel_subscriptions', display_name: 'Cancel Subscriptions', description: 'Can cancel subscriptions', category: 'Subscriptions', created_at: '2024-01-17T10:00:00Z' },
    
    // Pricing Plans
    { id: 23, name: 'view_pricing_plans', display_name: 'View Pricing Plans', description: 'Can view pricing plans', category: 'Pricing', created_at: '2024-01-18T10:00:00Z' },
    { id: 24, name: 'create_pricing_plans', display_name: 'Create Pricing Plans', description: 'Can create pricing plans', category: 'Pricing', created_at: '2024-01-18T10:00:00Z' },
    { id: 25, name: 'edit_pricing_plans', display_name: 'Edit Pricing Plans', description: 'Can edit pricing plans', category: 'Pricing', created_at: '2024-01-18T10:00:00Z' },
    { id: 26, name: 'delete_pricing_plans', display_name: 'Delete Pricing Plans', description: 'Can delete pricing plans', category: 'Pricing', created_at: '2024-01-18T10:00:00Z' },
    
    // Features
    { id: 27, name: 'view_features', display_name: 'View Features', description: 'Can view features', category: 'Features', created_at: '2024-01-19T10:00:00Z' },
    { id: 28, name: 'manage_features', display_name: 'Manage Features', description: 'Can manage features', category: 'Features', created_at: '2024-01-19T10:00:00Z' },
    
    // Email Templates
    { id: 29, name: 'view_email_templates', display_name: 'View Email Templates', description: 'Can view email templates', category: 'Email', created_at: '2024-01-20T10:00:00Z' },
    { id: 30, name: 'manage_email_templates', display_name: 'Manage Email Templates', description: 'Can manage email templates', category: 'Email', created_at: '2024-01-20T10:00:00Z' }
  ];

  const allCategories = [...new Set(samplePermissions.map(p => p.category))].sort();

  useEffect(() => {
    fetchPermissions();
    setCategories(allCategories);
  }, [searchTerm, filterCategory, currentPage, perPage]);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      
      const params = {
        search: searchTerm,
        category: filterCategory,
        page: currentPage,
        per_page: perPage
      };
      
      const response = await permissionService.list(params);
      
      if (response.data) {
        setPermissions(response.data);
        setTotalPermissions(response.meta?.total || response.data.length);
        setTotalPages(response.meta?.total_pages || Math.ceil(response.data.length / perPage));
        
        // Extract unique categories
        const uniqueCategories = [...new Set(response.data.map(p => p.category))].filter(Boolean).sort();
        if (uniqueCategories.length > 0) {
          setCategories(uniqueCategories);
        }
      } else {
        setPermissions([]);
      }
      
      setLoading(false);
    } catch (error) {
      
      // Fallback to sample data
      let filteredPermissions = [...samplePermissions];
      
      if (searchTerm) {
        filteredPermissions = filteredPermissions.filter(permission => 
          permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          permission.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          permission.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      if (filterCategory) {
        filteredPermissions = filteredPermissions.filter(permission => 
          permission.category === filterCategory
        );
      }
      
      setTotalPermissions(filteredPermissions.length);
      setTotalPages(Math.ceil(filteredPermissions.length / perPage));
      
      const startIndex = (currentPage - 1) * perPage;
      const endIndex = startIndex + perPage;
      filteredPermissions = filteredPermissions.slice(startIndex, endIndex);
      
      setPermissions(filteredPermissions);
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Permission name is required';
    } else if (!/^[a-z_]+$/.test(formData.name)) {
      errors.name = 'Permission name must be lowercase with underscores only';
    }
    
    if (!formData.display_name.trim()) {
      errors.display_name = 'Display name is required';
    }
    
    if (!formData.category.trim()) {
      errors.category = 'Category is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      if (!superAdmin) {
        toast.error('Only Super Admin can manage permissions');
        return;
      }
      
      if (selectedPermission) {
        await permissionService.update(selectedPermission.id, formData);
        toast.success('Permission updated successfully');
      } else {
        await permissionService.create(formData);
        toast.success('Permission created successfully');
      }
      
      setShowModal(false);
      resetForm();
      fetchPermissions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save permission');
    }
  };

  const handleEdit = (permission) => {
    setSelectedPermission(permission);
    setFormData({
      name: permission.name,
      display_name: permission.display_name,
      description: permission.description || '',
      category: permission.category
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      if (!superAdmin) {
        toast.error('Only Super Admin can delete permissions');
        return;
      }
      
      await permissionService.delete(selectedPermission.id);
      toast.success('Permission deleted successfully');
      setShowDeleteModal(false);
      setSelectedPermission(null);
      fetchPermissions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete permission');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      category: ''
    });
    setFormErrors({});
    setSelectedPermission(null);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      Users: Shield,
      Roles: Shield,
      Permissions: Key,
      Datarooms: Package,
      Subscriptions: Lock,
      Pricing: Lock,
      Features: Package,
      Email: Lock
    };
    return icons[category] || Lock;
  };

  const getCategoryColor = (category) => {
    const colors = {
      Users: 'bg-blue-100 text-blue-800',
      Roles: 'bg-purple-100 text-purple-800',
      Permissions: 'bg-indigo-100 text-indigo-800',
      Datarooms: 'bg-green-100 text-green-800',
      Subscriptions: 'bg-yellow-100 text-yellow-800',
      Pricing: 'bg-orange-100 text-orange-800',
      Features: 'bg-pink-100 text-pink-800',
      Email: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Permissions Management</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage system permissions and access controls
        </p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-4 rounded-lg shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Permissions</p>
              <p className="text-2xl font-bold text-gray-900">{samplePermissions.length}</p>
            </div>
            <Key className="h-8 w-8 text-blue-500" />
          </div>
        </motion.div>

        {allCategories.slice(0, 3).map((category, index) => {
          const count = samplePermissions.filter(p => p.category === category).length;
          const Icon = getCategoryIcon(category);
          return (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (index + 1) * 0.1 }}
              className="bg-white p-4 rounded-lg shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{category}</p>
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                </div>
                <Icon className="h-8 w-8 text-gray-400" />
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Header Actions */}
      <div className="flex flex-col lg:flex-row gap-4 justify-between mb-6">
          <div className="flex flex-col lg:flex-row gap-4 flex-1">
            <div className="flex-1">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search permissions..."
                  className="w-full pl-10 pr-4 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>

            <select
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>

            <select
              value={perPage}
              onChange={(e) => setPerPage(e.target.value)}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="15">15 per page</option>
              <option value="30">30 per page</option>
              <option value="50">50 per page</option>
            </select>
          </div>

          <div className="flex gap-3">
            <button
              onClick={fetchPermissions}
              className="bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors px-3 py-1.5 text-sm"
            >
              <RefreshCw className="w-5 h-5" />
            </button>

            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Permission
            </button>
          </div>
      </div>

      {/* Permissions Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permission
                </th>
                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="relative px-3 py-1.5">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4">
                    <TableLoader rows={5} columns={5} />
                  </td>
                </tr>
              ) : permissions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    No permissions found
                  </td>
                </tr>
              ) : (
                permissions.map((permission) => {
                  const Icon = getCategoryIcon(permission.category);
                  return (
                    <motion.tr
                      key={permission.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Key className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {permission.display_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {permission.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(permission.category)}`}>
                          <Icon className="w-3 h-3 mr-1" />
                          {permission.category}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          {permission.description || 'No description'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(permission.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(permission)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedPermission(permission);
                              setShowDeleteModal(true);
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * perPage + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * perPage, totalPermissions)}
                  </span>{' '}
                  of <span className="font-medium">{totalPermissions}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-3 py-1.5 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-3 py-1.5 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative"
          >
            <button
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold mb-4">
              {selectedPermission ? 'Edit Permission' : 'Create New Permission'}
            </h3>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Permission Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.name ? 'border-red-500' : ''
                  }`}
                  placeholder="e.g., view_reports"
                />
                {formErrors.name && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Use lowercase letters and underscores only
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.display_name ? 'border-red-500' : ''
                  }`}
                  placeholder="e.g., View Reports"
                />
                {formErrors.display_name && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.display_name}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    formErrors.category ? 'border-red-500' : ''
                  }`}
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                  <option value="Other">Other (New Category)</option>
                </select>
                {formErrors.category && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.category}</p>
                )}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-1.5 border rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Enter permission description..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white rounded-md hover:bg-blue-700 px-3 py-1.5 text-sm"
                >
                  {selectedPermission ? 'Update' : 'Create'} Permission
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedPermission && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative"
          >
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedPermission(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
              <h3 className="text-lg font-semibold">Delete Permission</h3>
            </div>
            
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete the permission "{selectedPermission.display_name}"? 
              This action cannot be undone.
            </p>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <div className="flex">
                <Info className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0" />
                <p className="text-sm text-yellow-800">
                  This permission may be assigned to roles. Deleting it will remove it from all roles.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedPermission(null);
                }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white rounded-md hover:bg-red-700 px-3 py-1.5 text-sm"
              >
                Delete Permission
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Permissions;